import { useMemo, useState } from 'react';
import { LoadingState } from '@v2/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    PiUploadSimpleBold, PiReceiptBold, PiCheckBold, PiArrowLeftBold,
    PiCurrencyDollarBold, PiTrendUpBold, PiChartLineUpBold,
    PiCalendarBold, PiStackBold, PiArrowRightBold, PiFunnelBold, PiXBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import SellerCommissionHomeSimple from '@v2/features/my-commission/SellerCommissionHomeSimple';
import SellerBucketGoalsCard from '@v2/features/my-commission/SellerBucketGoalsCard';
import { Sparkline } from './CommissionExecutiveCharts';
import {
    SellerChartPanel,
    SellerCommissionForecastChart,
    SellerMonthlyTable,
} from './SellerProfileCharts';
import type { CommissionRecord, SellerCommissionProfile } from './types';

const AVATAR_FALLBACK = '/img/favicon.png';

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function fmtShort(v: number) {
    if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
    return fmt(v);
}

function initials(name: string) {
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

const STATUS_STYLES: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    awaiting_nf: 'bg-amber-100 text-amber-800',
    nf_submitted: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-700',
};

function SellerAvatar({ name, url, size = 'lg' }: { name: string; url?: string | null; size?: 'sm' | 'lg' }) {
    const cls = size === 'lg'
        ? 'h-16 w-16 rounded-2xl text-xl ring-2 ring-white shadow-lg dark:ring-zinc-800'
        : 'h-10 w-10 rounded-xl text-sm ring-2 ring-white dark:ring-zinc-800';
    if (url) {
        return (
            <img src={url} alt={name}
                className={cn('shrink-0 object-cover', cls)}
                onError={(e) => { (e.target as HTMLImageElement).src = AVATAR_FALLBACK; }} />
        );
    }
    return (
        <span className={cn('flex shrink-0 items-center justify-center font-bold bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300', cls)}>
            {initials(name)}
        </span>
    );
}

function KpiCard({ label, value, sub, color = 'gray', icon: Icon }: {
    label: string; value: string; sub?: string;
    color?: 'gray' | 'emerald' | 'amber' | 'blue' | 'orange' | 'violet';
    icon?: React.ElementType;
}) {
    const colors: Record<string, string> = {
        gray: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
        emerald: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30',
        amber: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30',
        blue: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30',
        orange: 'border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30',
        violet: 'border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30',
    };
    const textColors: Record<string, string> = {
        gray: 'text-gray-500', emerald: 'text-emerald-700', amber: 'text-amber-700',
        blue: 'text-blue-700', orange: 'text-orange-700', violet: 'text-violet-700',
    };
    return (
        <div className={cn('rounded-xl border p-4', colors[color])}>
            <div className={cn('flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide mb-1', textColors[color])}>
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {label}
            </div>
            <p className="text-xl font-black text-gray-900 dark:text-white">{value}</p>
            {sub && <p className="mt-0.5 text-[11px] text-gray-500">{sub}</p>}
        </div>
    );
}

function DealInstallmentCard({ deal }: {
    deal: {
        deal_id: number;
        deal_number: string | null;
        internal_number?: string | null;
        display_sale_number?: string | null;
        deal_title: string | null;
        deal_amount: number;
        payment_parcelas: number | null;
        installment_label: string | null;
        installments_remaining: number;
        commission_rate_pp: number;
        seller_commission_per_installment: number;
        seller_commission_pending: number;
        seller_commission_received: number;
        seller_commission_cancelled?: number;
        seller_commission_total: number;
        mode_flags?: { key: string; label: string }[];
    };
}) {
    const total = deal.payment_parcelas ?? 1;
    const paid = total - deal.installments_remaining;
    const pct = total > 0 ? paid / total : 0;
    const saleLabel =
        deal.display_sale_number ||
        (deal.deal_amount > 0
            ? deal.internal_number || deal.deal_number || `#${deal.deal_id}`
            : deal.deal_number || `#${deal.deal_id}`);

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900 p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                        {saleLabel}
                    </p>
                    {deal.deal_title && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{deal.deal_title}</p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-0.5">
                        Deal: {fmt(deal.deal_amount)} · {deal.commission_rate_pp.toFixed(2)}% comissão
                        {deal.internal_number && deal.deal_number ? ` · Ploomes #${deal.deal_number}` : ''}
                    </p>
                    {deal.mode_flags && deal.mode_flags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {deal.mode_flags.map((f) => (
                                <span
                                    key={f.key}
                                    className={cn(
                                        'rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                                        f.key === 'exception' && 'bg-amber-100 text-amber-800',
                                        f.key === 'team' && 'bg-violet-100 text-violet-800',
                                        f.key === 'floor' && 'bg-rose-100 text-rose-800',
                                        f.key === 'carencia' && 'bg-red-50 text-red-700',
                                    )}
                                >
                                    {f.label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                    <Link
                        to={`/ploomes-deals/${deal.deal_id}?tab=commission`}
                        className="shrink-0 flex items-center gap-1 text-[10px] text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
                    >
                        Cronograma <PiArrowRightBold className="h-3 w-3" />
                    </Link>
            </div>

            <div className="mb-3">
                <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                    <span>{deal.installment_label ?? `${paid}/${total}`} parcelas</span>
                    <span>{Math.round(pct * 100)}% recebido</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-zinc-700 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pct * 100)}%` }} />
                </div>
                <div className="flex gap-px mt-1.5 overflow-x-auto">
                    {Array.from({ length: Math.min(total, 24) }).map((_, i) => (
                        <div
                            key={i}
                            className={cn(
                                'h-1.5 flex-1 rounded-sm',
                                i < paid ? 'bg-emerald-400' : i === paid ? 'bg-orange-400 animate-pulse' : 'bg-gray-200 dark:bg-zinc-600',
                            )}
                        />
                    ))}
                    {total > 24 && <span className="ml-1 text-[9px] text-gray-400 self-center">+{total - 24}</span>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
                    <p className="text-[10px] text-emerald-700 font-medium">Recebida</p>
                    <p className="font-bold text-emerald-900 dark:text-emerald-300">{fmtShort(deal.seller_commission_received)}</p>
                </div>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 px-3 py-2">
                    <p className="text-[10px] text-orange-700 font-medium">Futura · {deal.installments_remaining}×</p>
                    <p className="font-bold text-orange-900 dark:text-orange-300">{fmtShort(deal.seller_commission_pending)}</p>
                </div>
                {(deal.seller_commission_cancelled ?? 0) > 0 && (
                    <div className="col-span-2 rounded-lg bg-violet-50 px-3 py-2 dark:bg-violet-950/30">
                        <p className="text-[10px] font-medium text-violet-700">Comissão cancelada</p>
                        <p className="font-bold text-violet-900 line-through dark:text-violet-200">{fmtShort(deal.seller_commission_cancelled ?? 0)}</p>
                    </div>
                )}
            </div>

            {deal.seller_commission_per_installment > 0 && (
                <p className="mt-2 text-[10px] text-gray-400 text-center">
                    ≈ {fmt(deal.seller_commission_per_installment)} / parcela
                </p>
            )}
        </div>
    );
}

function InstallmentPipelineSection({ pipeline }: { pipeline: SellerCommissionProfile['installment_pipeline'] }) {
    if (!pipeline?.deals?.length) {
        return (
            <SellerChartPanel title="Cronograma de Parcelas" subtitle="Recebido · Futuro por deal">
                <p className="text-xs text-gray-400 text-center py-6">Nenhum deal com parcelamento encontrado.</p>
            </SellerChartPanel>
        );
    }

    return (
        <SellerChartPanel title="Cronograma de Parcelas" subtitle="Recebido · Futuro por deal">
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="rounded-lg bg-gray-50 dark:bg-zinc-800 px-2 py-2.5">
                    <p className="text-[10px] text-gray-500 font-medium uppercase">Total</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{fmtShort(pipeline.commission_total)}</p>
                    <p className="text-[9px] text-gray-400">{pipeline.installments_total} parcelas</p>
                </div>
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-2 py-2.5">
                    <p className="text-[10px] text-emerald-700 font-medium uppercase">Recebida</p>
                    <p className="text-sm font-bold text-emerald-900 dark:text-emerald-300">{fmtShort(pipeline.commission_received)}</p>
                    <p className="text-[9px] text-emerald-700">{pipeline.installments_considered_paid} pagas</p>
                </div>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 px-2 py-2.5">
                    <p className="text-[10px] text-orange-700 font-medium uppercase">Futura</p>
                    <p className="text-sm font-bold text-orange-900 dark:text-orange-300">{fmtShort(pipeline.commission_pending)}</p>
                    <p className="text-[9px] text-orange-700">{pipeline.installments_remaining} restantes</p>
                </div>
                {(pipeline.commission_cancelled ?? 0) > 0 && (
                    <div className="col-span-3 rounded-lg bg-violet-50 px-2 py-2.5 dark:bg-violet-950/30">
                        <p className="text-[10px] font-medium uppercase text-violet-700">Comissão cancelada</p>
                        <p className="text-sm font-bold text-violet-900 line-through dark:text-violet-200">{fmtShort(pipeline.commission_cancelled ?? 0)}</p>
                    </div>
                )}
            </div>
            <div className="space-y-3 max-h-[560px] overflow-y-auto pr-0.5">
                {pipeline.deals.map((d) => (
                    <DealInstallmentCard key={d.deal_id} deal={d} />
                ))}
            </div>
        </SellerChartPanel>
    );
}

function NfUploadLink({ recordId, monthlyNfId }: { recordId: number; monthlyNfId: number | null }) {
    const { t } = useI18n();
    const to = monthlyNfId
        ? `/my-commission/monthly-nf/${monthlyNfId}`
        : `/my-commission/submit-nf/${recordId}`;
    return (
        <Link to={to} className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700">
            <PiUploadSimpleBold className="size-3.5" />
            {monthlyNfId ? t('v2.my_commission.action_nf_month') : t('v2.my_commission.action_nf')}
        </Link>
    );
}

type Tab = 'upcoming' | 'paid' | 'all';

function RecordsTable({ records, showUpload }: { records: CommissionRecord[]; showUpload: boolean }) {
    const { t } = useI18n();
    const seenMonthlyNf = new Set<number>();

    if (records.length === 0) {
        return <p className="py-10 text-center text-sm text-gray-400">Nenhum registro nesta aba.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="border-b border-gray-100 dark:border-zinc-800 text-xs text-gray-500">
                    <tr>
                        <th className="px-4 py-3 text-left font-medium">Deal</th>
                        <th className="px-4 py-3 text-left font-medium">Parcela</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                        <th className="px-4 py-3 text-left font-medium">Nível</th>
                        <th className="px-4 py-3 text-left font-medium">Data</th>
                        <th className="px-4 py-3 text-right font-medium">Comissão</th>
                        {showUpload && <th className="px-4 py-3 text-center font-medium">Ação</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                    {records.map((r) => {
                        const canUpload = showUpload && ['awaiting_nf', 'pending'].includes(r.status);
                        const monthlyId = r.commission_monthly_nf_id ?? null;
                        let showNfAction = canUpload;
                        if (canUpload && monthlyId) {
                            if (seenMonthlyNf.has(monthlyId)) {
                                showNfAction = false;
                            } else {
                                seenMonthlyNf.add(monthlyId);
                            }
                        }
                        return (
                            <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors">
                                <td className="px-4 py-3">
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {r.display_sale_number ||
                                            (Number(r.deal_amount) > 0
                                                ? r.internal_number || r.deal_number || `#${r.ploomes_deal_id}`
                                                : r.deal_number || `#${r.ploomes_deal_id}`)}
                                    </p>
                                    {r.deal_title && <p className="text-xs text-gray-500 max-w-[180px] truncate">{r.deal_title}</p>}
                                    {r.deal_amount != null && (
                                        <p className="text-[10px] text-gray-400">
                                            Deal: {fmtShort(r.deal_amount)}
                                            {r.internal_number && r.deal_number ? ` · Ploomes #${r.deal_number}` : ''}
                                        </p>
                                    )}
                                    {r.mode_flags && r.mode_flags.length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {r.mode_flags.map((f) => (
                                                <span
                                                    key={f.key}
                                                    className={cn(
                                                        'rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                                                        f.key === 'exception' && 'bg-amber-100 text-amber-800',
                                                        f.key === 'team' && 'bg-violet-100 text-violet-800',
                                                        f.key === 'floor' && 'bg-rose-100 text-rose-800',
                                                        f.key === 'carencia' && 'bg-red-50 text-red-700',
                                                    )}
                                                >
                                                    {f.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    {r.installment_label
                                        ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">{r.installment_label}</span>
                                        : <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_STYLES[r.status] ?? STATUS_STYLES.pending)}>
                                        {canUpload && monthlyId
                                            ? t('v2.my_commission.status_in_monthly_nf')
                                            : r.status_label}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-medium text-orange-700">{r.role_label}</span>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">
                                    <span>{r.created_at.split(' ')[0]}</span>
                                    {r.paid_at && <span className="block text-emerald-600">Pago {r.paid_at.split(' ')[0]}</span>}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                                    {fmt(r.final_amount)}
                                </td>
                                {showUpload && (
                                    <td className="px-4 py-3 text-center">
                                        {showNfAction && <NfUploadLink recordId={r.id} monthlyNfId={monthlyId} />}
                                        {r.status === 'nf_submitted' && (
                                            <span className="inline-flex items-center gap-1 text-[10px] text-blue-700">
                                                <PiCheckBold className="size-3" /> {t('v2.my_commission.status_waiting_rh')}
                                            </span>
                                        )}
                                        {r.status === 'paid' && (
                                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                                                <PiCheckBold className="size-3" /> {t('v2.my_commission.status_paid')}
                                            </span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Date filter helpers ──────────────────────────────────────────────────────

type DateRange = { from: string; to: string };

const toISO = (d: Date) => d.toISOString().slice(0, 10);

const PRESETS: { label: string; range: () => DateRange }[] = [
    {
        label: 'Este mês',
        range: () => {
            const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
            return { from: toISO(new Date(y, m, 1)), to: toISO(new Date(y, m + 1, 0)) };
        },
    },
    {
        label: 'Últ. 3 meses',
        range: () => {
            const d = new Date(); const from = new Date(d);
            from.setMonth(from.getMonth() - 2); from.setDate(1);
            return { from: toISO(from), to: toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
        },
    },
    {
        label: 'Este ano',
        range: () => { const y = new Date().getFullYear(); return { from: `${y}-01-01`, to: `${y}-12-31` }; },
    },
    {
        label: 'Ano passado',
        range: () => { const y = new Date().getFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31` }; },
    },
];

function DateRangeFilter({ from, to, onChange, onClear }: {
    from: string; to: string;
    onChange: (r: DateRange) => void;
    onClear: () => void;
}) {
    const hasFilter = !!(from || to);
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400">
                <PiFunnelBold className="h-3.5 w-3.5" />
                Período
            </span>
            {PRESETS.map((p) => (
                <button
                    key={p.label}
                    type="button"
                    onClick={() => onChange(p.range())}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-300 transition-colors"
                >
                    {p.label}
                </button>
            ))}
            <div className="flex items-center gap-1.5">
                <input
                    type="date"
                    value={from}
                    onChange={(e) => onChange({ from: e.target.value, to })}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-200"
                />
                <span className="text-xs text-gray-400">até</span>
                <input
                    type="date"
                    value={to}
                    onChange={(e) => onChange({ from, to: e.target.value })}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-200"
                />
            </div>
            {hasFilter && (
                <button
                    type="button"
                    onClick={onClear}
                    className="flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700 hover:bg-orange-200 dark:bg-orange-950/40 dark:text-orange-400"
                >
                    <PiXBold className="h-3 w-3" /> Limpar
                </button>
            )}
        </div>
    );
}

export default function SellerHistoryPage() {
    const { t } = useI18n();
    const { userId } = useParams<{ userId: string }>();
    const [searchParams] = useSearchParams();
    const isSelf = !userId;
    const showFull = searchParams.get('view') === 'full';
    const recordsParam = searchParams.get('records');
    const initialTab: Tab = recordsParam === 'paid' || recordsParam === 'all' || recordsParam === 'upcoming'
        ? recordsParam
        : 'upcoming';
    const [tab, setTab] = useState<Tab>(initialTab);
    const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });

    const hasFilter = !!(dateRange.from || dateRange.to);

    const { data, isLoading, isError } = useQuery<{ data: SellerCommissionProfile }>({
        queryKey: ['commissioning', 'seller-profile', userId ?? 'me', dateRange.from, dateRange.to, showFull],
        queryFn: () => {
            const params: Record<string, string> = {};
            if (dateRange.from) params.from = dateRange.from;
            if (dateRange.to) params.to = dateRange.to;
            if (showFull) params.include = 'records';
            return userId
                ? api.get(`/commissioning/sellers/${userId}/profile`, { params }).then((r) => r.data)
                : api.get('/commissioning/my-seller-profile', { params }).then((r) => r.data);
        },
    });

    const profile = data?.data;
    const s = profile?.summary;
    const isOwnProfile = isSelf || window.__V2__?.user?.id === profile?.user.id;

    const paidSparkline = useMemo(() => profile?.monthly.map((m) => m.paid) ?? [], [profile?.monthly]);

    const tabRecords = tab === 'upcoming'
        ? profile?.records.upcoming ?? []
        : tab === 'paid'
            ? profile?.records.paid ?? []
            : profile?.records.all ?? [];

    const tabs: { id: Tab; label: string; count: number }[] = [
        { id: 'upcoming', label: t('v2.my_commission.tab_upcoming'), count: profile?.records.upcoming.length ?? 0 },
        { id: 'paid', label: t('v2.my_commission.tab_paid'), count: profile?.records.paid.length ?? 0 },
        { id: 'all', label: t('v2.my_commission.tab_all'), count: profile?.records.all.length ?? 0 },
    ];

    const payoutRate = s && s.accrued_mtd > 0 ? Math.round((s.paid_mtd / s.accrued_mtd) * 100) : 0;
    const pipeline = profile?.installment_pipeline;
    const simpleHomePath = userId ? `/commissioning/sellers/${userId}` : '/my-commission';

    if (! showFull) {
        return (
            <SellerCommissionHomeSimple
                profile={profile}
                isSelf={isSelf}
                userId={userId}
                isLoading={isLoading}
                isError={isError}
                backToRecords={! isSelf}
            />
        );
    }

    return (
        <div className="w-full min-h-screen space-y-5 pb-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Link
                    to={simpleHomePath}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-orange-700 hover:underline dark:text-orange-400"
                >
                    <PiArrowLeftBold className="size-4" />
                    {t('v2.my_commission.back_simple')}
                </Link>
                {!isSelf && (
                    <Link to="/commissioning/records" className="text-sm text-gray-600 hover:underline dark:text-zinc-400">
                        {t('v2.my_commission.back_records')}
                    </Link>
                )}
            </div>

            {/* Filtro de período */}
            <div className="flex justify-end">
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                    <DateRangeFilter
                        from={dateRange.from}
                        to={dateRange.to}
                        onChange={setDateRange}
                        onClear={() => setDateRange({ from: '', to: '' })}
                    />
                    {hasFilter && (
                        <p className="mt-2 text-right text-[11px] font-medium text-orange-700">
                            {dateRange.from || '—'} até {dateRange.to || '—'}
                        </p>
                    )}
                </div>
            </div>

            {isLoading && <LoadingState variant="dashboard" />}
            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {t('v2.my_commission.load_error')}
                </div>
            )}

            {profile && s && (
                <>
                    {/* HERO */}
                    <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-orange-50 to-white p-5 dark:border-zinc-700 dark:from-orange-950/20 dark:to-zinc-900">
                        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                            <div className="flex items-center gap-4">
                                <SellerAvatar name={profile.user.name} url={profile.user.avatar_url} size="lg" />
                                <div>
                                    <h1 className="text-xl font-black text-gray-900 dark:text-white">{profile.user.name}</h1>
                                    {profile.user.job_title && <p className="text-sm text-gray-600">{profile.user.job_title}</p>}
                                    {profile.team && (
                                        <p className="mt-0.5 text-xs text-orange-700">
                                            Equipe {profile.team.team_name}
                                            {profile.team.manager_name && ` · ${profile.team.manager_name.split(' ')[0]}`}
                                        </p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-400">{s.deals_total} deals · {s.records_total} registros</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 rounded-xl bg-white px-5 py-3 shadow-sm ring-1 ring-orange-100 sm:ml-auto dark:bg-zinc-900 dark:ring-orange-900/30">
                                <div>
                                    {s.total_paid_lifetime > 0 ? (
                                        <>
                                            <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                                <PiCurrencyDollarBold className="size-3.5" />
                                                Total ganho
                                            </div>
                                            <p className="text-3xl font-black text-gray-900 dark:text-white">{fmtShort(s.total_paid_lifetime)}</p>
                                            {s.total_pipeline > 0 && (
                                                <p className="mt-0.5 text-[11px] font-semibold text-amber-600">+ {fmtShort(s.total_pipeline)} a receber</p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                                                <PiCurrencyDollarBold className="size-3.5" />
                                                Total gerado
                                            </div>
                                            <p className="text-3xl font-black text-gray-900 dark:text-white">{fmtShort(s.total_pipeline + s.total_paid_lifetime)}</p>
                                            <p className="mt-0.5 text-[11px] font-semibold text-amber-600">Aguardando pagamento</p>
                                        </>
                                    )}
                                </div>
                                {paidSparkline.some((v) => v > 0) ? (
                                    <Sparkline values={paidSparkline.slice(-8)} />
                                ) : (
                                    profile.monthly.some((m) => m.accrued > 0) && (
                                        <Sparkline values={profile.monthly.slice(-8).map((m) => m.accrued)} />
                                    )
                                )}
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                            <KpiCard label="Mês (pago)" value={fmtShort(s.paid_mtd)} sub={`Gerado ${fmtShort(s.accrued_mtd)}`} color="emerald" icon={PiTrendUpBold} />
                            <KpiCard label="A receber" value={fmtShort(s.total_pipeline)} sub="Registros abertos" color="amber" icon={PiReceiptBold} />
                            <KpiCard label="Parcelas futuras" value={fmtShort(pipeline?.commission_pending ?? 0)} sub={`${pipeline?.installments_remaining ?? 0} parc. · ${pipeline?.deals?.length ?? 0} deals`} color="orange" icon={PiCalendarBold} />
                            <KpiCard label="YTD pago" value={fmtShort(s.paid_ytd)} sub={`${payoutRate}% eficiência`} color="blue" icon={PiChartLineUpBold} />
                            <KpiCard label="NF enviada" value={fmtShort(s.nf_submitted_amount)} sub={`${s.nf_submitted_count} aguard. RH`} color="violet" icon={PiStackBold} />
                            <KpiCard label="Aguard. NF" value={fmtShort(s.awaiting_nf_amount)} sub={`${s.awaiting_nf_count} registros`} color="gray" icon={PiReceiptBold} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                        <aside className="space-y-4 lg:col-span-5">
                            <SellerBucketGoalsCard userId={userId} />
                            <SellerChartPanel title="Histórico mensal" subtitle="Pago vs gerado · clique no mês">
                                <SellerMonthlyTable months={profile.monthly} userId={userId} />
                            </SellerChartPanel>
                        </aside>

                        <section id="pipeline" className="scroll-mt-4 lg:col-span-7">
                            <InstallmentPipelineSection pipeline={profile.installment_pipeline} />
                        </section>
                    </div>

                    <section id="commission-forecast" className="scroll-mt-4">
                        <SellerChartPanel
                            title="Previsibilidade de comissão"
                            subtitle="Próximos 12 meses · valores = sua comissão (não a parcela do cliente) · clique no mês"
                        >
                            <SellerCommissionForecastChart forecast={pipeline?.forecast} />
                        </SellerChartPanel>
                    </section>

                    <div id="records" className="scroll-mt-4 rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex border-b border-gray-100 dark:border-zinc-800">
                            {tabs.map((tabItem) => (
                                <button
                                    key={tabItem.id}
                                    type="button"
                                    onClick={() => setTab(tabItem.id)}
                                    className={cn(
                                        'relative flex-1 py-3.5 text-center text-sm font-semibold transition',
                                        tab === tabItem.id ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700',
                                    )}
                                >
                                    {tabItem.label} ({tabItem.count})
                                    {tab === tabItem.id && <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-orange-500" />}
                                </button>
                            ))}
                        </div>
                        <RecordsTable records={tabRecords} showUpload={isOwnProfile} />
                    </div>
                </>
            )}
        </div>
    );
}
